import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCampaignDetail, submitTweet } from '../services/campaignService';
import { useData } from '../contexts/DataContext';
import useMediaQuery from '../hooks/useMediaQuery';
import SubmissionReceipt, { getSafeTweetUrl, formatTimestamp } from '../components/ui/SubmissionReceipt';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysLeft(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  const days = Math.ceil(diff / 86400000);
  if (days < 0) return 'Expired';
  if (days === 0) return 'Due today';
  return `${days} day${days !== 1 ? 's' : ''} left`;
}

function formatDeadlineShort(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
}

function getSubmitStatus(campaign) {
  // Backend returns flat deliveryStatus
  const ds = campaign.deliveryStatus;
  if (!ds || ds === 'TO_DO') return 'not_submitted';
  if (ds === 'POSTED') return 'under_review';
  if (ds === 'VERIFIED') return 'verified';
  if (ds === 'PAID') return 'paid';
  return 'not_submitted';
}

const SUBMIT_STATUS_LABELS = {
  under_review: 'Post Submitted — Under Review',
  verified: 'Post Submitted — Verified',
  paid: 'Post Submitted — Paid',
};

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { invalidate } = useData();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tweetUrl, setTweetUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const load = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await getCampaignDetail(id);
      setCampaign(res.data);
    } catch {
      setError('Failed to load campaign details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleSubmit = async () => {
    const safeUrl = getSafeTweetUrl(tweetUrl);
    if (!safeUrl) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitTweet(id, safeUrl);
      if (res.data?.receipt) {
        setCampaign(prev => ({ ...prev, receipt: res.data.receipt, deliveryStatus: res.data.unchanged ? prev.deliveryStatus : 'POSTED' }));
      }
      setTweetUrl('');
      invalidate('campaigns');
      invalidate('payments');
      invalidate('stats');
      invalidate('activity');
      await load({ silent: true });
    } catch (err) {
      setSubmitError(err?.response?.data?.error || 'We could not confirm your submission. Check the URL and try again; repeating the same post is safe.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !campaign) return <div className="loading-state">Loading...</div>;
  if (error && !campaign) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }
  if (!campaign) return null;

  // Backend returns flat fields: endDate, payment, deliveryStatus, tweet, briefSummary, briefUrl, etc.
  const deadline = campaign.deadlineAt || campaign.endDate;
  const payment = campaign.payment;
  const status = getSubmitStatus(campaign);
  const receipt = campaign.receipt || (campaign.tweet?.url ? { ...campaign.tweet, history: [] } : null);
  const submitted = !!receipt;
  const canEdit = submitted ? campaign.canReplace === true : campaign.canSubmit === true;

  const sectionPad = 24;
  const headerPad = isMobile ? 24 : 32;

  return (
    <>
      <button className="brief-back" onClick={() => navigate('/campaigns')}>
        &larr; Back to Campaigns
      </button>
      {error && <div role="alert" className="brief-section" style={{ padding: sectionPad }}>
        {error} Your last loaded campaign is shown. <button onClick={() => load({ silent: true })}>Refresh campaign</button>
      </div>}

      <div className="brief-header" style={isMobile ? { gridTemplateColumns: '1fr', padding: headerPad } : { padding: headerPad }}>
        <div>
          <div className="brief-brand">{campaign.name}</div>
          <div className="brief-campaign">{campaign.campaignType || ''}</div>
        </div>
        {deadline && (
          <div className="brief-deadline" style={isMobile ? { textAlign: 'left', marginTop: 12 } : {}}>
            <div className="brief-deadline-label">Deadline</div>
            <div className="brief-deadline-val">{formatDeadlineShort(deadline)}</div>
            <div className="brief-deadline-days">{daysLeft(deadline)}</div>
          </div>
        )}
      </div>

      {campaign.briefSummary && (
        <div className="brief-section" style={{ padding: sectionPad }}>
          <div className="brief-section-title">Campaign Brief</div>
          <div className="brief-section-body">
            <p>{campaign.briefSummary}</p>
          </div>
        </div>
      )}

      {(campaign.briefUrl || campaign.notionBriefUrl) && (
        <div className="brief-section" style={{ padding: sectionPad }}>
          <div className="brief-section-title">Reference Material</div>
          <div className="brief-section-body">
            {campaign.briefUrl && (
              <a href={campaign.briefUrl} target="_blank" rel="noopener noreferrer" className="brief-ref">
                Campaign Brief
              </a>
            )}
            {campaign.notionBriefUrl && (
              <a href={campaign.notionBriefUrl} target="_blank" rel="noopener noreferrer" className="brief-ref">
                Notion Brief
              </a>
            )}
          </div>
        </div>
      )}

      <div className="brief-section" style={{ padding: sectionPad }}>
        <div className="brief-section-title">Requirements</div>
        <div className="brief-section-body">
          <div className="req-grid">
            {campaign.campaignType && (
              <div>
                <div className="req-label">Type</div>
                <div className="req-val">{campaign.campaignType}</div>
              </div>
            )}
            {payment != null && (
              <div>
                <div className="req-label">Payment</div>
                <div className="req-val gold">{formatCurrency(payment)} USDC</div>
              </div>
            )}
            {deadline && (
              <div>
                <div className="req-label">Deadline</div>
                <div className="req-val">{formatTimestamp(deadline)} (your local time)</div>
              </div>
            )}
            {campaign.paymentNetwork && (
              <div>
                <div className="req-label">Network</div>
                <div className="req-val">{campaign.paymentNetwork}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SubmissionReceipt receipt={receipt} status={SUBMIT_STATUS_LABELS[status] || 'Post received'} padding={isMobile ? 24 : 32} />
      {!canEdit && campaign.blockedReason && (
        <div className="brief-section" style={{ padding: sectionPad }} role="status">{campaign.blockedReason}</div>
      )}
      {canEdit && (
        <div className="submit-section" style={{ padding: isMobile ? 24 : 32 }}>
          <div className="submit-title">{submitted ? 'Replace Your Post' : 'Submit Your Content'}</div>
          <div className="submit-desc">{submitted ? 'Paste a new post URL to replace your entry. Previous post metrics and verification will reset; your receipt keeps the history.' : 'Paste the URL of your published tweet or thread below.'}</div>
          <input
            className="submit-input"
            value={tweetUrl}
            aria-label={submitted ? 'Replacement post URL' : 'Post URL'}
            onChange={(e) => setTweetUrl(e.target.value)}
            placeholder="https://x.com/yourhandle/status/..."
          />
          {submitError && <p role="alert">{submitError}</p>}
          <button
            className="submit-btn"
            disabled={!getSafeTweetUrl(tweetUrl) || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting...' : submitted ? 'Replace Post' : 'Submit Post'}
          </button>
        </div>
      )}
    </>
  );
}
